import { networkStatus } from "@Src/constants";
import { Types } from "../actionTypes";
import { createSelector } from "@reduxjs/toolkit";
export const initialState = {
  data: null,
  nearbyLocations: {
    distance: "10mi",
    api: {
      status: null,
      error: null,
    },
    stores: [],
    furthestOfferDistance: null,
  },
  recent: [],
  isSearching: false,
  resultsPerSearch: 180,
  totalPages: 1,
  currentPage: 1,
  numberPerPage: 20,
  totalBrands: 0,
  totalOffers: 0,
  fetchingSuggestions: false,
  searchSuggestions: [],
  error: false,
  error_code: null,
  categoriesToShow: [],
  selectedSuggestion: {
    logo: "",
    storeKey: "",
    term: "",
  },
  storesSuggested: [],
};

const searchResults = (state = initialState, action) => {
  switch (action.type) {
    case Types.FETCH_SEARCH_RESULTS: {
      return {
        ...state,
        isSearching: true,
        error: false,
        error_code: null,
      };
    }
    case Types.FETCH_SEARCH_RESULTS_SUCCESS: {
      const { isSubCatSearch, response } = action;

      let categoriesToShow = state.categoriesToShow;

      // for showing subcategories
      // do not update visible subcategories if the last filter change was a subcategory
      // and that subcategory has results
      if (!isSubCatSearch) {
        categoriesToShow = !!response.category_count
          ? Object.entries(response.category_count)
              .filter(([, value]) => value > 0)
              .map(([key]) => key)
          : [];
      }

      return {
        ...state,
        data: response,
        currentPage: 1,
        isSearching: false,
        totalBrands: response.info.total_brands,
        totalOffers: response.info.total_offers,
        categoriesToShow,
      };
    }
    case Types.FETCH_SEARCH_RESULTS_FAILURE: {
      const { error } = action;
      return {
        ...state,
        data: null,
        error: true,
        error_code: error.data?.error_code,
        isSearching: false,
      };
    }
    case Types.CHANGE_RESULTS_PAGE: {
      const { page } = action;
      return { ...state, currentPage: page };
    }
    case Types.FETCH_SEARCH_SUGGESTIONS: {
      return {
        ...state,
        fetchingSuggestions: true,
      };
    }
    case Types.FETCH_SEARCH_SUGGESTIONS_SUCCESS: {
      return {
        ...state,
        fetchingSuggestions: false,
        searchSuggestions: [...action.response],
      };
    }
    case Types.CLEAR_SEARCH_SUGGESTIONS:
    case Types.RESET_CATEGORY:
    case Types.FILTER_RESET_CATEGORY:
    case Types.FILTER_CATEGORY_CHANGE: {
      return {
        ...state,
        searchSuggestions: [],
      };
    }
    case Types.FILTER_CHANGING_HOW_TO_REDEEM:
    case Types.FILTER_CHANGING_DISTANCE:
    case Types.FILTER_CHANGING_ONLINE:
      return {
        ...state,
        currentPage: 1,
      };
    case Types.GET_NEARBY_LOCATIONS_OFFERS:
      return {
        ...state,
        nearbyLocations: {
          ...state.nearbyLocations,
          api: {
            status: networkStatus.IN_PROGRESS,
            error: null,
          },
        },
      };
    case Types.GET_NEARBY_LOCATIONS_OFFERS_SUCCESS:
      const lastOffer =
        action.payload &&
        action.payload.stores &&
        action.payload.stores[action.payload.stores.length - 1];

      return {
        ...state,
        nearbyLocations: {
          ...state.nearbyLocations,
          api: {
            ...state.nearbyLocations.api,
            status: networkStatus.SUCCESS,
            error: null,
          },
          stores: action.payload.stores || [],
          furthestOfferDistance: lastOffer ? +lastOffer.closest_location_distance : null,
        },
      };
    case Types.GET_NEARBY_LOCATIONS_OFFERS_FAILURE:
      return {
        ...state,
        nearbyLocations: {
          ...state.nearbyLocations,
          api: {
            ...state.nearbyLocations.api,
            status: networkStatus.ERROR_OTHER,
            error: action.error,
          },
        },
      };
    case Types.SET_NEARBY_LOCATIONS_DISTANCE:
      return {
        ...state,
        nearbyLocations: {
          ...state.nearbyLocations,
          distance: action.payload,
        },
      };
    case Types.FETCH_RECENT_SEARCHES_SUCCESS:
      return {
        ...state,
        recent: action.payload,
      };
    case Types.SIGN_OUT_SUCCESS:
      return {
        ...state,
        recent: initialState.recent,
      };
    case Types.STORE_SELECTED_SUGGESTION_DATA: {
      const { data } = action;
      return {
        ...state,
        selectedSuggestion: {
          storeKey: data.store_key,
          logo: data.store_logo_url,
          term: data.term,
        },
      };
    }
    case Types.CLEAR_SELECTED_SUGGESTION_DATA: {
      return {
        ...state,
        selectedSuggestion: { ...initialState.selectedSuggestion },
      };
    }
    case Types.ADD_SUGGESTED_STORE: {
      return {
        ...state,
        storesSuggested: [...state.storesSuggested, action.data],
      };
    }
    default:
      return state;
  }
};

//selectors
export const getStoresSuggested = (state) => state.storesSuggested;
export const searchingForResults = (state) => state.isSearching;
export const searchingResultsError = (state) => state.error;
export const searchingResultsErrorCode = (state) => state.error_code;
export const getStores = (state) => (state.data && state.data.stores ? state.data.stores : []);
export const getCurrentPageStores = createSelector([(state) => state], (state) => {
  const lowerLimit = (state.currentPage - 1) * 20;
  const upperLimit = lowerLimit + state.numberPerPage;
  if (!state.data || !state.data.stores) {
    return [];
  }
  return state.data.stores.filter((store, index) => index >= lowerLimit && index + 1 <= upperLimit);
});
export const getSuggestionStoreKey = (state) => state.selectedSuggestion.storeKey;

export const getSelectedSuggestion = (state) => state.selectedSuggestion;

export const getRelatedPopularDeals = (state) => state.data?.related?.popular;

export const getRelatedTopDeals = (state) => state.data?.related?.top;

export const getStoresNearLocation = (state) => state.data?.related?.stores;
export const getNearbyLocations = (state) => state.nearbyLocations;
export const getNearbyStores = (state) => state.nearbyLocations.stores;

export const getCurrentPage = (state) => state.currentPage;
export const getTotalPages = createSelector([(state) => state.data], (data) => {
  if (!data || !data.stores || Math.ceil(data.stores.length / 20) === 0) {
    return 1;
  }
  return Math.ceil(data.stores.length / 20);
});

export const getResultsPerSearch = (state) => state.resultsPerSearch;
export const getNumberPerPage = (state) => state.numberPerPage;
export const getCategoryKeys = createSelector([(state) => state.data], (data) => {
  return data ? data.category_count : {};
});
export const getDistances = createSelector([(state) => state.data], (data) => {
  return data ? data.distances_count : {};
});
export const getInfo = (state) => (state.data ? state.data.info : {});
export const getRemptionMethods = createSelector([(state) => state.data], (data) => {
  return data ? data.how_to_redeem_count : {};
});

export const getTotalBrands = (state) => state.totalBrands;

export const getTotalOffers = (state) => state.totalOffers;
export const getFetchingSuggestions = (state) => state.fetchingSuggestions;
export const getSearchSuggestionsValue = (state) => {
  if (state.searchSuggestions.length > 0) {
    // Return 10 results max - capitalize first letter
    return state.searchSuggestions.slice(0, 10).map((suggestion, index) => {
      let capitalizedSuggestion =
        suggestion.term.charAt(0).toUpperCase() + suggestion.term.slice(1);
      return capitalizedSuggestion;
    });
  } else {
    return state.searchSuggestions;
  }
};

export const getSearchSuggestionsData = createSelector(
  [(state) => state.searchSuggestions],
  (searchSuggestions) => searchSuggestions.slice(0, 10),
);

export const getSearchSelectedLogo = (state) => state.selectedSuggestion.logo;
export const getCategoriesToShow = (state) => state.categoriesToShow;

export const getRecentSearches = (state) => state.recent;

export default searchResults; // the reducer
